<template>
    <div id="download-co">
        <div id="app-layout-right-column__content__title">
            <span>Download Capture One Style</span>
        </div>

        <PlanUpgradeNotificationInSidebar v-if="isCurrentPlanFree" />
        <div v-else class="py-5">
            <label class="mb-2" for="title" theme="--start">Style Name</label>
            <div class="input-container" name="input-container--name">
                <v-text-field
                    rounded
                    v-model="$v.title.$model"
                    name="title"
                    ref="name"
                    placeholder="Eg. My Style #5"
                />
            </div>

            <div class="option-container mb-1">
                <div class="label-container">
                    <span class="label">Platform:</span>
                    <i class="far fa-question-circle ml-1" name="info-platform"></i>
                    <tippy to="info-platform" theme="info" :delay="[0, 0]">
                        Select the appropriate option depending on your operating system - macOS or Windows
                    </tippy>
                </div>
                <v-menu offset-y min-width="120" content-class="modal-menu">
                    <template v-slot:activator="{ on }">
                        <div class="menu-label" v-on="on">
                            <span class="name">{{ platform }}</span>
                            <i class="material-icons">arrow_drop_down</i>
                        </div>
                    </template>
                    <v-list>
                        <v-list-item v-for="selectablePlatform in platforms" :key="selectablePlatform"
                                     @click="platform = selectablePlatform"
                                     :class="{'--selected': selectablePlatform == platform}">
                            <v-list-item-title>{{ selectablePlatform }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>

            <div class="option-container mb-1">
                <div class="label-container">
                    <span class="label">Image type:</span>
                    <i class="far fa-question-circle ml-1" name="info-type"></i>
                    <tippy to="info-type" theme="info" :delay="[0, 0]">
                        This option will make the Style suitable for work with Raw or Non-Raw files, depending on your
                        selection. Make sure this matches the type of images you will be working on, otherwise your
                        results might be sub-optimal.
                    </tippy>
                </div>
                <v-menu offset-y min-width="120" content-class="modal-menu">
                    <template v-slot:activator="{ on }">
                        <div class="menu-label" v-on="on">
                            <span class="name">{{ type }}</span>
                            <i class="material-icons">arrow_drop_down</i>
                        </div>
                    </template>
                    <v-list>
                        <v-list-item v-for="selectableType in types" :key="selectableType"
                                     @click="type = selectableType" :class="{'--selected': selectableType == type}">
                            <v-list-item-title>{{ selectableType }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>

            <div class="option-container">
                <div class="label-container">
                    <span class="label">Camera Make:</span>
                    <i class="far fa-question-circle ml-1" name="info-camera"></i>
                    <tippy to="info-camera" theme="info" :delay="[0, 0]">
                        Please select the appropriate option according to your camera. If your camera is not listed, please select the Generic option.
                    </tippy>
                </div>
                <v-menu offset-y min-width="120" content-class="modal-menu"
                        :disabled="type === 'Non-Raw'"
                >
                    <template v-slot:activator="{ on }">
                        <div class="menu-label" v-on="on" :class="{'--disabled': type === 'Non-Raw'}">
                            <span class="name">{{ camera.name }}</span>
                            <i class="material-icons">arrow_drop_down</i>
                        </div>
                    </template>
                    <v-list>
                        <v-list-item v-for="selectableCamera in cameras" :key="selectableCamera.name"
                                     @click="camera = selectableCamera" :class="{'--selected': selectableCamera == camera}">
                            <v-list-item-title>{{ selectableCamera.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>

            <div class="buttons-container mt-10">
                <button type="button" class="button button--primary" @click="onSubmit" :disabled="exporting || !title">
                    <v-progress-circular v-if="exporting" indeterminate color="var(--color-white)" size="18"
                                         width="3"></v-progress-circular>
                    <span v-else class="font-weight-bold">Export</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { mapGetters, mapState } from "vuex";
import { v4 as uuidv4 } from 'uuid';
import JSZip from "jszip";
import download from "downloadjs";
import PlanUpgradeNotificationInSidebar from "@/components/app/ui/PlanUpgradeNotificationInSidebar.vue";

export default {
    components: {PlanUpgradeNotificationInSidebar},
    data() {
        return {
            types: [
                'Raw',
                'Non-Raw'
            ],
            platforms: [
                'MacOS',
                'Windows'
            ],
            cameras: [
                {
                    name: 'Canon - CR2',
                    lut: 'Canon'
                },
                {
                    name: 'Canon - CR3',
                    lut: 'Fujifilm'
                },
                {
                    name: 'Fujifilm',
                    lut: 'Leica'
                },
                {
                    name: 'Generic',
                    lut: 'Raw-Generic'
                },
                {
                    name: 'Leica',
                    lut: 'Leica'
                },
                {
                    name: 'Nikon',
                    lut: 'Nikon'
                },
                {
                    name: 'Olympus',
                    lut: 'Olympus'
                },
                {
                    name: 'Panasonic',
                    lut: 'Olympus'
                },
                {
                    name: 'Pentax',
                    lut: 'Pentax'
                },
                {
                    name: 'Phase One',
                    lut: 'PhaseOne'
                },
                {
                    name: 'Sigma',
                    lut: 'Sigma'
                },
                {
                    name: 'Sony',
                    lut: 'Sony'
                },
                {
                    name: 'Zeiss',
                    lut: 'Zeiss'
                },
            ],
            type: 'Raw',
            platform: 'MacOS',
            camera: {
                name: 'Generic',
                lut: 'Raw-Generic'
            },
            nonRawCamera: {
                name: 'Non-Raw',
                lut: 'Non-Raw'
            },
            title: "",
            exporting: false,
        };
    },

    validations: {
        title: {required},
    },

    computed: {
        ...mapState("Projects", ["action"]),
        ...mapState("UI", ["rightColumn"]),
        ...mapGetters('User', ['isCurrentPlanFree']),
    },

    watch: {
        action(newValue) {
            if (this.action.action == "export-co-from-asset" && this.action.data.length == 1) {
                this.title = newValue.data[0].nicename;
            }
        },

        rightColumn(newValue) {
            if (newValue == 'download-co') {
                this.$refs.name.focus();
            }
        }
    },

    methods: {
        async onSubmit() {
            if (this.$v.$invalid) {
                return;
            }

            this.exporting = true;

            let data = {
                name: this.title,
                options: {
                    ACESinput: true,
                    ACESoutput: true,
                    CO: true,
                    isRaw: this.type === 'Raw',
                    camera: this.type === "Raw" ? this.camera : this.nonRawCamera
                },
            }

            const files = await window.luts.prepareDownloadLuts(data);

            const xmlContent = `
            <?xml version="1.0"?>
            <SL Engine="1300">
                \t<E K="ICCProfile" V="${this.title}.icc" />
                \t<E K="Name" V="${this.title}" />
                \t<E K="StyleSource" V="Styles" />
                \t<E K="UUID" V="${uuidv4()}" />
            </SL>
            `;

            const costyleFile = new File([xmlContent], '.costyle', {type: "text/plain"});
            const iccFilename = this.platform === 'MacOS' ? `${this.title}.costyle.${this.title}.icc` : `${this.title}.${this.title}.icc`;

            let zip = new JSZip();
            zip.file(iccFilename, files[0]);
            zip.file(`${this.title}.costyle`, costyleFile);

            const title = this.title

            zip.generateAsync({type: "blob"}).then(blob => download(blob, `${title}.zip`));

            this.exporting = false;
            this.title = "";
        },
    },
};
</script>

<style lang="scss">
#download-co {
    .switch-container {
        margin-left: initial;
    }

    label {
        //margin-bottom: 10px !important;
        line-height: 1;
        display: block;
        height: auto;
        padding: 0;
        font-size: 1rem;
    }

    .v-input {
        padding: 0;
        margin: 0;
    }

    .v-input__slot {
        padding: 0;
    }

    input[type="text"] {
        background: white;
        -webkit-appearance: none;
        border-radius: 5px;
        border: none;
        color: var(--color-bg-dark);
        padding: 7px 10px;
        //margin-bottom: 20px;
        font-size: 1rem;

        &::placeholder {
            color: var(--font-default-color);
            font-family: Poppins;
            font-size: 1rem;
        }
    }

    input[type="submit"],
    button {
        margin: 0 auto;
        outline: 0;

        &:hover {
            opacity: 0.8;
        }
    }

    .option-container {
        display: flex;
        align-items: center;

        .label-container {
            min-width: 110px;
            display: flex;
            align-items: center;
        }
    }

    .menu-label {
        display: flex;
        align-items: center;
        font-size: 13px;
        position: relative;
        cursor: pointer;

        .name {
            line-height: 1;
            font-size: 1rem;
        }

        i {
            font-size: 18px;
        }
    }

    .option-container {
        display: flex;
        align-items: center;
        gap: 1rem;

        &.--disabled {
            opacity: 0.25;

            input {
                pointer-events: none;
                cursor: not-allowed;
            }
        }

        .option-title {
            line-height: 1.25;
        }
    }

    .btn-advanced-options {
        margin-bottom: 0;

        > span {
            line-height: 20px;
        }
    }

    input {
        line-height: 20px;
    }

    .description-container {
        margin-bottom: 8px;

        .v-expansion-panel-header {
            span {
                line-height: 12px;
            }
        }
    }

    div[name="export-lut-name-container"] {
        input {
            background: white;
            border-radius: 5px;
            border: none;
            color: #121212;
            padding: 7px 10px;
            font-size: 1rem;
            width: 100%;

            &:disabled {
                opacity: 0.3;
                cursor: not-allowed;
            }
        }
    }

    .buttons-container {
        margin-top: 20px;
        display: flex;
        justify-content: center;
    }

    .v-expansion-panel {
        background: transparent;
        color: inherit;

        &:before {
            display: none;
        }

        .v-expansion-panel-header {
            padding: 0;
            min-height: auto;
            flex-direction: row-reverse;
            margin-bottom: 10px;

            &:before {
                display: none;
            }

            .v-expansion-panel-header__icon i {
                font-size: 18px;
                color: var(--font-default-color);
            }
        }

        .v-expansion-panel-content__wrap {
            padding: 0;
        }
    }
}

.modal-menu {
    z-index: 10 !important;
    border-radius: var(--box-rounding);
    //padding: 25px 0;
    background: var(--dark-gray);

    .v-list {
        background: var(--dark-gray);
        padding: 25px 0;

        .v-list-item {
            min-height: 20px;
            padding: 0 25px;
            //display: grid;
            //grid-template-columns: 18px 1fr 18px;
            cursor: pointer;

            &.--first {
                order: -1;
            }

            &:hover {
                background: rgb(54, 54, 54);
            }

            &.full-span {
                grid-template-columns: 1fr;
            }

            &.stars-icons-container {
                grid-template-columns: repeat(5, 1fr);
            }

            &[aria-expanded="true"] {
                background: rgb(54, 54, 54);
            }

            &.--selected {
                .material-icons,
                .v-list-item__title {
                    color: var(--primary);
                }
            }

            &.v-list-item--disabled {
                opacity: 0.6;
            }

            .material-icons {
                color: #989898;
                font-size: 18px;
                width: 18px;
            }

            .v-list-item__title {
                margin-left: 4px;
                color: #989898;
                font-size: 1rem;
            }

            &:after {
                display: none;
            }
        }
    }
}
</style>